import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Paper } from '@mui/material';
import { Lock } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

/**
 * Route wrapper that gates a page on the viewer's role.
 *
 * The enforceable counterpart to the sidebar hiding an admin-only link (see
 * the `roles` filter in Sidebar.tsx): the sidebar link disappears, and this
 * stops a direct URL from reaching the page shell anyway. The API is the
 * real backstop (every one of these routes already requires SUPER_ADMIN or
 * ADMIN server-side), so this guard is purely a presentation improvement —
 * a wrong-role visitor sees one clear message instead of a page that mounts
 * and then fills with 403 errors from every data call it makes.
 */
const RoleGuard: React.FC<{ roles: string[]; children: React.ReactNode }> = ({ roles, children }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (user?.role && roles.includes(user.role)) return <>{children}</>;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', px: 2 }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 480,
          textAlign: 'center',
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            mx: 'auto',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'action.hover',
            color: 'text.secondary',
          }}
        >
          <Lock sx={{ fontSize: 30 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Restricted to workspace admins
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          This area is only available to workspace administrators. If you need access, ask your workspace owner.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/home')}>
          Back to my workspace
        </Button>
      </Paper>
    </Box>
  );
};

export default RoleGuard;
